import { useState } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Link, useNavigate } from 'react-router-dom';

const VendorManagementOverview = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('monthly');

  // Professional data - minimal colors
  const vendorSpendData = [
    { name: 'Food', current: 125000, previous: 110000 },
    { name: 'Beverage', current: 98000, previous: 105000 },
    { name: 'Housekeeping', current: 75000, previous: 70000 },
    { name: 'Maintenance', current: 45000, previous: 42000 },
    { name: 'IT Services', current: 35000, previous: 38000 },
  ];

  const paymentTrendData = [
    { month: 'Jan', paid: 285000, pending: 120000 },
    { month: 'Feb', paid: 320000, pending: 95000 },
    { month: 'Mar', paid: 295000, pending: 110000 },
    { month: 'Apr', paid: 410000, pending: 85000 },
    { month: 'May', paid: 380000, pending: 78000 },
    { month: 'Jun', paid: 425000, pending: 65000 },
  ];

  const vendorDistributionData = [
    { name: 'Active', value: 45, color: '#4CAF50' },
    { name: 'Inactive', value: 8, color: '#757575' },
    { name: 'Pending', value: 12, color: '#FF9800' },
    { name: 'On Hold', value: 5, color: '#F44336' },
  ];

  const performanceData = [
    { name: 'Fresh Foods', score: 98 },
    { name: 'Beverage Co.', score: 96 },
    { name: 'CleanPro', score: 94 },
    { name: 'TechSol', score: 89 },
    { name: 'OfficeSup', score: 97 },
  ];

  const statsCards = [
    {
      title: 'Total Vendors',
      value: '70',
      subtitle: '45 Active',
    },
    {
      title: 'Monthly Spend',
      value: '₹3.8L',
      subtitle: '2% decrease',
    },
    {
      title: 'Outstanding',
      value: '₹1.2L',
      subtitle: '32 invoices',
    },
    {
      title: 'Avg Payment Days',
      value: '28',
      subtitle: 'Target: 30',
    },
  ];

  const recentActivity = [
    {
      vendor: 'Global Supplies Inc.',
      action: 'New registration',
      time: '2 hours ago',
      type: 'info',
    },
    {
      vendor: 'Food Distributors Ltd.',
      action: 'Contract renewed',
      time: '1 day ago',
      type: 'success',
    },
    { vendor: 'Tech Solutions', action: 'Document expired', time: '2 days ago', type: 'warning' },
    { vendor: 'CleanPro Services', action: 'Payment processed', time: '3 days ago', type: 'info' },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Vendor Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/vendor-management')}
            className="btn text-black font-bold"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/vendor-management/accounts')}
            className="btn"
          >
            Vendor Accounts
          </button>
          {/* <button
            onClick={() => navigate("/dashboard/mdm/vendor-management/purchase-orders")}
            className="btn"
          >
            Purchase Orders
          </button>
          <button
            onClick={() => navigate("/dashboard/mdm/vendor-management/payments")}
            className="btn"
          >
            Payments
          </button> */}
        </div>
      </header>

      {/* Time Range Filter */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Vendor Management</h2>
        <div className="flex space-x-2">
          {['weekly', 'monthly', 'quarterly', 'yearly'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 rounded text-sm ${
                timeRange === range
                  ? 'bg-gray-800 text-white'
                  : 'bg-white text-gray-700 border hover:bg-gray-100'
              }`}
            >
              {range.charAt(0).toUpperCase() + range.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
        {statsCards.map((card, index) => (
          <div key={index} className="bg-white p-4 rounded border">
            <p className="text-gray-600 text-sm mb-1">{card.title}</p>
            <h3 className="text-2xl font-bold text-gray-800 mb-1">{card.value}</h3>
            <p className="text-gray-500 text-xs">{card.subtitle}</p>
          </div>
        ))}
      </div>
      {/* Key Metrics */}
      <div className="mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'On-time Delivery', value: '94%', trend: '+2%' },
            { label: 'Invoice Accuracy', value: '96%', trend: '+1%' },
            { label: 'Quality Score', value: '92%', trend: '+3%' },
            { label: 'Cost Efficiency', value: '88%', trend: '-1%' },
          ].map((metric, index) => (
            <div key={index} className=" p-4 border rounded bg-white">
              <p className="text-sm text-gray-600 mb-1">{metric.label}</p>
              <p className="text-2xl font-bold text-gray-800">{metric.value}</p>
              <p
                className={`text-xs ${
                  metric.trend.startsWith('+') ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {metric.trend} from last period
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Vendor Spend Comparison */}
        <div className="bg-white p-6 rounded border">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Vendor Spend Comparison</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={vendorSpendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="current" name="Current Period" fill="#374151" />
              <Bar dataKey="previous" name="Previous Period" fill="#9CA3AF" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Payment Trends */}
        <div className="bg-white p-6 rounded border">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Payment Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={paymentTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="paid"
                name="Paid"
                stroke="#374151"
                fill="#374151"
                fillOpacity={0.1}
              />
              <Area
                type="monotone"
                dataKey="pending"
                name="Pending"
                stroke="#9CA3AF"
                fill="#9CA3AF"
                fillOpacity={0.1}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Vendor Distribution */}
        <div className="bg-white p-6 rounded border">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Vendor Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={vendorDistributionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {vendorDistributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top Performers */}
        <div className="bg-white p-6 rounded border">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Top Performing Vendors</h3>
          <div className="space-y-4">
            {performanceData.map((vendor, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{vendor.name}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gray-800 h-2 rounded-full"
                      style={{ width: `${vendor.score}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-gray-800 w-8">{vendor.score}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white p-6 rounded border">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Recent Activity</h3>
          <div className="space-y-3">
            {recentActivity.map((activity, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 pb-3 border-b border-gray-200 last:border-b-0"
              >
                <div
                  className={`w-2 h-2 rounded-full mt-2 ${
                    activity.type === 'success'
                      ? 'bg-green-500'
                      : activity.type === 'warning'
                        ? 'bg-yellow-500'
                        : 'bg-gray-500'
                  }`}
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">{activity.vendor}</p>
                  <p className="text-sm text-gray-600">{activity.action}</p>
                  <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorManagementOverview;
